import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import SearchPage from 'containers/SearchPage'
import { Table } from '@arnsbogroup/react-voicearchive'
import useApi from 'hooks/useApi'
import messages from './messages'

const FeatureFlagToggles = ({ userId, userName }) => {
  const [featureFlags, , , refetch] = useApi(`/rest/v2/featureflag/${userId}`)

  const handleToggle = (id, enabled) => {
    const next = featureFlags.map((flag) =>
      flag.id === id ? { ...flag, enabled } : flag
    )
    refetch({ method: 'PUT', body: JSON.stringify(next) })
  }

  if (!featureFlags) return null

  return (
    <div>
      <h3 className="text-base font-semibold text-gray-900 mb-4">
        <FormattedMessage {...messages.flagsFor} values={{ name: userName }} />
      </h3>

      {featureFlags.length === 0 && (
        <p className="text-sm text-gray-500">
          <FormattedMessage {...messages.noFlags} />
        </p>
      )}

      <div className="divide-y divide-gray-200">
        {featureFlags.map(({ id, name, description, enabled }) => (
          <div key={id} className="py-4 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">{name}</p>
              {description && (
                <p className="text-sm text-gray-500">{description}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => handleToggle(id, !enabled)}
              className={`${enabled ? 'bg-primary' : 'bg-gray-200'} relative inline-flex shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
              aria-pressed={enabled}
            >
              <span className="sr-only">{name}</span>
              <span
                aria-hidden="true"
                className={`${enabled ? 'translate-x-5' : 'translate-x-0'} inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
              />
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

const FeatureFlaggingPage = (props) => {
  const { formatMessage } = useIntl()
  const [selectedUser, setSelectedUser] = useState(null)

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
        <h2 className="text-lg font-semibold leading-6 text-gray-900">
          <FormattedMessage {...messages.title} />
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          <FormattedMessage {...messages.subtitle} />
        </p>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className="w-1/2 border-r border-gray-200 overflow-auto">
          <SearchPage
            {...props}
            baseApiUrl="/rest/v2/featureflag"
            containerClassName="w-full"
            components={{
              Tr: ({ childProps = {}, ...innerProps }) => (
                <Table.Tr
                  {...innerProps}
                  onClick={childProps.uid ? () => setSelectedUser(childProps) : undefined}
                  className={{
                    'cursor-pointer': !!childProps.uid,
                    'bg-blue-50': !!(childProps.uid && selectedUser?.uid === childProps.uid),
                  }}
                />
              ),
            }}
            head={{
              name: { label: formatMessage(messages.columnName) },
              email: { label: formatMessage(messages.columnEmail) },
            }}
            body={{
              name: (u) => <span className="text-sm font-medium text-gray-900">{u.name}</span>,
              email: (u) => <span className="text-sm text-gray-500">{u.email}</span>,
            }}
          />
        </div>

        <div className="w-1/2 overflow-auto p-6">
          {selectedUser ? (
            <FeatureFlagToggles
              key={selectedUser.uid}
              userId={selectedUser.uid}
              userName={selectedUser.name}
            />
          ) : (
            <p className="text-sm text-gray-500 mt-4">
              <FormattedMessage {...messages.noUserSelected} />
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default FeatureFlaggingPage
